import React, { FC, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import type { BulkMarkAsPaymentStatus } from '@sequencehq/core-components'
import {
  DeleteInvoicesDialog,
  FinalizeInvoicesDialog,
  InvoiceBulkActionBar,
  MarkAsSentInvoicesDialog,
  MergeInvoicesDialog,
  Popover,
  RecalculateInvoicesDialog,
  SendInvoicesDialog,
  VoidInvoicesDialog
} from '@sequencehq/core-components'
import { ShadowL } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import invariant from 'tiny-invariant'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useNotifications } from '@sequencehq/core-components'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import {
  toInvoicePaymentStatusBadgeProps,
  useDisclosure
} from '@sequencehq/utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { SelectedInvoicesTotal } from './SelectedInvoicesTotal'

type Invoice = Dashboardv99990101Api.GetInvoicesListSummary.InvoiceListSummary

export type InvoiceBulkActionsProps = {
  children: React.ReactNode
  selectedRows: Invoice[]
  clearSelectedRows: () => void
  popoverOffset?: [number, number]
}

type Action =
  | 'finalize'
  | 'send'
  | 'void'
  | 'delete'
  | 'merge'
  | 'markAs'
  | 'markAsSent'
  | 'finalizeAndSend'
  | 'recalculate'
  | 'download'

/**
 * The bulk-download endpoint rejects a request for more than this many invoices
 * (MAX_INVOICES in DownloadMultipleInvoicesUseCase). The download action is
 * disabled above the cap so the user sees why up front instead of triggering a
 * request the backend will reject.
 */
const MAX_DOWNLOAD_INVOICES = 200

/**
 * Bulk actions that ask for confirmation before running. The remaining
 * actions (`markAs`, `finalizeAndSend`) run without a dialog.
 */
type ConfirmableAction = Extract<
  Action,
  | 'recalculate'
  | 'finalize'
  | 'send'
  | 'markAsSent'
  | 'void'
  | 'delete'
  | 'merge'
>

export const InvoiceBulkActions: FC<InvoiceBulkActionsProps> = ({
  children,
  selectedRows,
  clearSelectedRows,
  popoverOffset
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure()
  const [confirmingAction, setConfirmingAction] =
    useState<ConfirmableAction | null>(null)
  const { displayNotification } = useNotifications()
  const mergeInvoices = useMutation(dashboard20240730Client.postMergeInvoices)
  const downloadInvoices = useMutation(
    dashboard20240730Client.postDownloadMultipleInvoices
  )
  const flags = useFeatureFlags()

  //['finalize', 'send', 'send', 'markAs']

  // if selected row is draft - posible actions are finalize and send
  // if selected row is final - posible actions are send
  // if selected row is sent - possible actions are mark as

  // filter out actions that are not possible

  // if action is finalize filter selected rows to only include draft
  // if action is send filter selected rows to only include draft and final

  // if selectedRows is empty, close the popover if it's open

  const filterSelectedRows = (action: Action) => {
    switch (action) {
      case 'finalize':
        return selectedRows.filter(row => row.status === 'DRAFT')

      case 'recalculate':
        return selectedRows.filter(
          row =>
            ['IN_PROGRESS', 'DRAFT'].includes(row.status) &&
            !!row.billingScheduleId
        )

      case 'send':
        return selectedRows.filter(
          row => row.status === 'DRAFT' || row.status === 'FINAL'
        )

      case 'markAs':
        return selectedRows.filter(row => row.status === 'SENT')

      case 'markAsSent':
        return selectedRows.filter(
          row => row.status === 'DRAFT' || row.status === 'FINAL'
        )

      case 'void':
        return selectedRows.filter(
          row => row.status === 'DRAFT' || row.status === 'FINAL'
        )

      case 'delete':
        return selectedRows.filter(row => row.status === 'DRAFT')

      case 'download':
        // Every invoice has a downloadable PDF regardless of status.
        return selectedRows

      case 'merge':
        return selectedRows.filter(row => row.status !== 'DRAFT').length ===
          0 &&
          new Set(selectedRows.map(row => row.customerId)).size === 1 &&
          new Set(selectedRows.map(row => row.currency)).size === 1 &&
          selectedRows.length > 1
          ? selectedRows
          : []
    }

    return selectedRows
  }

  const getSelectedRowsIds = (selected: Invoice[]) => {
    return selected.map(row => row.id)
  }

  useEffect(() => {
    if (selectedRows.length > 0 && !isOpen) {
      onToggle()
    }
    if (selectedRows.length === 0 && isOpen) {
      onClose()
    }
  }, [selectedRows, isOpen, onToggle, onClose])

  const finalizeSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const finalizeBulkActionRes =
      await dashboard20240730Client.postFinalizeMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (finalizeBulkActionRes.error) {
      return displayNotification('Unable to finalize invoices', {
        type: 'error'
      })
    }

    onClose()
    clearSelectedRows()
  }

  const recalculateSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const recalculateBulkActionRes =
      await dashboard20240730Client.postRecalculateMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (recalculateBulkActionRes.error) {
      clearSelectedRows()
      return displayNotification('Unable to recalculate invoices', {
        type: 'error'
      })
    }

    onClose()
    clearSelectedRows()
  }

  const finalizeAndSendSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const finalizeAndSendBulkActionRes =
      await dashboard20240730Client.postFinalizeAndSendMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (finalizeAndSendBulkActionRes.error) {
      return displayNotification('Unable to finalize and send invoices', {
        type: 'error'
      })
    }

    clearSelectedRows()
    onClose()
  }

  const sendSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const sendBulkActionRes =
      await dashboard20240730Client.postSendMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (sendBulkActionRes.error) {
      displayNotification('Unable to send invoices', { type: 'error' })
      return
    }

    clearSelectedRows()
    onClose()
  }

  const downloadSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    // Download fires inline with no confirmation dialog, so clear the selection
    // up front (the ids are already captured). This unmounts the bar instantly
    // via the empty-selection guard above, so it doesn't linger over the async
    // request while the in-flight download panel loads.
    clearSelectedRows()

    try {
      await downloadInvoices.mutateAsync({ invoiceIds: filteredIds })
    } catch {
      displayNotification('Unable to download invoices', { type: 'error' })
    }
  }

  const voidSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const voidMultipleInvoicesBulkActionRes =
      await dashboard20240730Client.postVoidMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (voidMultipleInvoicesBulkActionRes.error) {
      return displayNotification('Unable to void invoices', { type: 'error' })
    }

    clearSelectedRows()
    onClose()
  }

  const deleteSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const deleteMultipleInvoicesBulkActionRes =
      await dashboard20240730Client.postDeleteMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (deleteMultipleInvoicesBulkActionRes.error) {
      return displayNotification('Unable to delete invoices', { type: 'error' })
    }

    clearSelectedRows()
    onClose()
  }

  const markAsSentSelectedInvoices = async (filtered: Invoice[]) => {
    const filteredIds = getSelectedRowsIds(filtered)

    const markAsSentBulkActionRes =
      await dashboard20240730Client.postMarkAsSentMultipleInvoices({
        invoiceIds: filteredIds
      })

    if (markAsSentBulkActionRes.error) {
      return displayNotification('Unable to mark invoices as sent', {
        type: 'error'
      })
    }

    clearSelectedRows()
    onClose()
  }

  const mergeSelectedInvoices = async (filtered: Invoice[]) => {
    const invoiceIds = filtered.map(invoice => invoice.id)

    try {
      const response = await mergeInvoices.mutateAsync({
        invoiceIds: invoiceIds
      })

      invariant(response, 'Failed to merge invoices')
    } catch {
      displayNotification('Unable to merge invoices', { type: 'error' })
    }
  }

  const markAsSelectedInvoices = async (
    status: BulkMarkAsPaymentStatus,
    filtered: Invoice[]
  ) => {
    const filteredIds: string[] = getSelectedRowsIds(filtered)

    const updateStatusRes =
      await dashboard20240730Client.putUpdatePaymentStatusMultipleInvoices({
        invoiceIds: filteredIds,
        paymentStatus: status
      })

    if (updateStatusRes.error) {
      return displayNotification('Unable to update invoice payment statuses', {
        type: 'error'
      })
    }

    clearSelectedRows()
    onClose()
  }

  const handleSubmitRows = (action: Action) => {
    const filteredSelectedRows = filterSelectedRows(action)

    if (filteredSelectedRows.length > 0) {
      switch (action) {
        case 'finalize':
          return finalizeSelectedInvoices(filteredSelectedRows)

        case 'recalculate':
          return recalculateSelectedInvoices(filteredSelectedRows)

        case 'markAsSent':
          return markAsSentSelectedInvoices(filteredSelectedRows)

        case 'send': {
          const draftSelectedRows = filteredSelectedRows.filter(
            row => row.status === 'DRAFT'
          )

          const sendSelectedRows = filteredSelectedRows.filter(
            row => row.status !== 'DRAFT'
          )

          if (!sendSelectedRows.length) {
            return finalizeAndSendSelectedInvoices(draftSelectedRows)
          }

          if (draftSelectedRows.length && sendSelectedRows.length) {
            return Promise.all([
              finalizeAndSendSelectedInvoices(draftSelectedRows),
              sendSelectedInvoices(sendSelectedRows)
            ])
          }

          return sendSelectedInvoices(filteredSelectedRows)
        }

        case 'void':
          return voidSelectedInvoices(filteredSelectedRows)

        case 'delete':
          return deleteSelectedInvoices(filteredSelectedRows)

        case 'download':
          return downloadSelectedInvoices(filteredSelectedRows)

        case 'merge':
          return mergeSelectedInvoices(filteredSelectedRows)
      }
    }
    return
  }

  const handleMarkAsRows = (status: BulkMarkAsPaymentStatus) => {
    const filtered = filterSelectedRows('markAs')

    if (getSelectedRowsIds(selectedRows).length > 0) {
      return markAsSelectedInvoices(status, filtered)
    }
    return
  }

  const handleCloseBulkActions = () => {
    clearSelectedRows()
    onClose()
  }

  const sortActions = () => {
    const actions: Action[] = []
    if (selectedRows.some(row => row.status === 'DRAFT')) {
      actions.push('finalize', 'send', 'markAsSent', 'void', 'delete')
    }
    if (
      selectedRows.some(
        row =>
          ['IN_PROGRESS', 'DRAFT'].includes(row.status) &&
          !!row.billingScheduleId
      )
    ) {
      actions.push('recalculate')
    }
    if (selectedRows.some(row => row.status === 'FINAL')) {
      if (actions.includes('finalize')) {
        actions.filter(action => action !== 'finalize')
        actions.push('send', 'markAsSent', 'void')
      }
      actions.push('send', 'markAsSent', 'void')
    }
    if (selectedRows.some(row => row.status === 'SENT')) {
      actions.push('markAs')
    }

    // Any selected invoice can be downloaded regardless of status.
    if (selectedRows.length > 0) {
      actions.push('download')
    }

    const uniqueArray = [...new Set(actions)]
    return uniqueArray
  }

  const possibleActions = sortActions()

  const countFor = (action: Action) => {
    return filterSelectedRows(action).length
  }

  /**
   * If any of the available sub actions have a total applicable number of invoices
   * fewer than the total selected number, we want to show the total next to
   * all of them (if they have a total to show, that is!)
   */
  const showCountOnBulkActions = possibleActions.some(
    action => countFor(action) < selectedRows.length
  )

  const { can } = usePermissions()

  const showCountToDisplayForAction = (action: Action) => {
    if (!showCountOnBulkActions) return false
    return countFor(action) !== 0
  }

  const isOverDownloadCap = countFor('download') > MAX_DOWNLOAD_INVOICES

  // The status bar always renders this component (even with nothing selected),
  // relying on the popover's isOpen to hide it. But closing via isOpen plays the
  // popover's exit fade, which leaves a greyed-out ghost of the bar on screen —
  // very visible on download, where the in-flight panel appears at the same time.
  // Returning null instead unmounts the popover outright, so clearing the
  // selection removes the bar instantly with no fade.
  if (selectedRows.length === 0) {
    return null
  }

  return (
    <>
      <Popover.Root
        isOpen={isOpen}
        onClose={onClose}
        placement="top"
        closeOnBlur={false}
        offset={popoverOffset}
      >
        <Popover.Anchor>{children}</Popover.Anchor>
        <Popover.Content
          width="100%"
          py={1}
          px={2}
          borderRadius="8px"
          boxShadow={ShadowL}
        >
          <Popover.Body width="100%" height="100%" p="0">
            <InvoiceBulkActionBar
              count={selectedRows.length}
              summary={<SelectedInvoicesTotal invoices={selectedRows} />}
              onDismiss={handleCloseBulkActions}
            >
              {(possibleActions.includes('finalize') ||
                possibleActions.includes('send') ||
                possibleActions.includes('recalculate')) && (
                <>
                  <ProtectedButton permission="org.invoice:update">
                    <InvoiceBulkActionBar.RecalculateButton
                      showCount={showCountToDisplayForAction('recalculate')}
                      count={countFor('recalculate')}
                      onClick={() => setConfirmingAction('recalculate')}
                    />
                  </ProtectedButton>
                  <ProtectedButton permission="org.invoice:finalize">
                    <InvoiceBulkActionBar.FinalizeButton
                      showCount={showCountToDisplayForAction('finalize')}
                      count={countFor('finalize')}
                      onClick={() => setConfirmingAction('finalize')}
                    />
                  </ProtectedButton>
                  <ProtectedButton permission="org.invoice:send">
                    <InvoiceBulkActionBar.SendButton
                      showCount={showCountToDisplayForAction('send')}
                      count={countFor('send')}
                      onClick={() => setConfirmingAction('send')}
                    />
                  </ProtectedButton>
                </>
              )}

              {possibleActions.includes('markAs') &&
                can('org.invoice:update') && (
                  <InvoiceBulkActionBar.MarkAsDropdown
                    showCount={showCountToDisplayForAction('markAs')}
                    count={countFor('markAs')}
                    onSelect={status => handleMarkAsRows(status)}
                  />
                )}

              {(can('org.invoice:update') ||
                can('org.invoice:delete') ||
                can('org.invoice:read')) && (
                <InvoiceBulkActionBar.KebabMenu
                  items={[
                    ...(can('org.invoice:read')
                      ? [
                          <InvoiceBulkActionBar.KebabMenu.Download
                            key="download"
                            // Download always applies to the whole selection, so
                            // its count is never ambiguous — show it whenever
                            // something is selected, independent of the shared
                            // disambiguation heuristic the other actions use.
                            showCount={countFor('download') > 0}
                            count={countFor('download')}
                            isDisabled={isOverDownloadCap}
                            tooltip={
                              isOverDownloadCap
                                ? `A maximum of ${MAX_DOWNLOAD_INVOICES} invoices can be downloaded`
                                : undefined
                            }
                            onClick={() => void handleSubmitRows('download')}
                          />
                        ]
                      : []),
                    ...(can('org.invoice:create')
                      ? [
                          <InvoiceBulkActionBar.KebabMenu.Merge
                            key="merge"
                            showCount={showCountToDisplayForAction('merge')}
                            count={countFor('merge')}
                            onClick={() => setConfirmingAction('merge')}
                          />
                        ]
                      : []),
                    ...(can('org.invoice:update')
                      ? [
                          <InvoiceBulkActionBar.KebabMenu.MarkAsSent
                            key="markAsSent"
                            showCount={showCountToDisplayForAction(
                              'markAsSent'
                            )}
                            count={countFor('markAsSent')}
                            onClick={() => setConfirmingAction('markAsSent')}
                          />,
                          <InvoiceBulkActionBar.KebabMenu.Void
                            key="void"
                            showCount={showCountToDisplayForAction('void')}
                            count={countFor('void')}
                            onClick={() => setConfirmingAction('void')}
                          />
                        ]
                      : []),
                    ...(can('org.invoice:delete')
                      ? [
                          <InvoiceBulkActionBar.KebabMenu.Delete
                            key="delete"
                            showCount={showCountToDisplayForAction('delete')}
                            count={countFor('delete')}
                            onClick={() => setConfirmingAction('delete')}
                          />
                        ]
                      : [])
                  ]}
                />
              )}
            </InvoiceBulkActionBar>
          </Popover.Body>
        </Popover.Content>
      </Popover.Root>
      {confirmingAction && (
        <ConfirmBulkActionDialog
          action={confirmingAction}
          count={countFor(confirmingAction)}
          onConfirm={() => void handleSubmitRows(confirmingAction)}
          onClose={() => setConfirmingAction(null)}
        />
      )}
    </>
  )
}

const ConfirmBulkActionDialog: FC<{
  action: ConfirmableAction
  count: number
  onConfirm: () => void
  onClose: () => void
}> = ({ action, ...props }) => {
  const DialogComponent = match(action)
    .with('recalculate', () => RecalculateInvoicesDialog)
    .with('finalize', () => FinalizeInvoicesDialog)
    .with('send', () => SendInvoicesDialog)
    .with('markAsSent', () => MarkAsSentInvoicesDialog)
    .with('void', () => VoidInvoicesDialog)
    .with('delete', () => DeleteInvoicesDialog)
    .with('merge', () => MergeInvoicesDialog)
    .exhaustive()

  return <DialogComponent {...props} isOpen />
}
